/**
 * Helpers for question doc links that carry the `isSourceSop` provenance
 * marker. Shared by the V1 (Cloudscape) and V2 (Stencil) question flows so the
 * "is this the source SOP" rule lives in one place.
 */
import type { DocLink } from 'src/types/helios/Question';

/**
 * Stored title of a source-SOP doc link. Persisted with the question (not an
 * i18n key) so every view that lists doc links — V1 and V2, edit and read-only
 * — shows the same label, and it still reads well after a copy demotes the
 * link to an ordinary one.
 */
export const SOURCE_SOP_DOC_TITLE = 'Reference doc';

/** Whether a doc link is the source SOP its question was generated from. */
export function isSourceSopLink(link: DocLink): boolean {
  return link.isSourceSop === true;
}

/**
 * The same link as an ordinary, author-owned doc link: title and URL kept, the
 * `isSourceSop` marker dropped. Used when copying a question — a copy is a new
 * MANUAL question that was not generated from that SOP, so it must not claim
 * the provenance, but the author keeps the reference document as a normal link.
 */
export function toAuthorDocLink(link: DocLink): DocLink {
  // Shallow copy, then drop the marker (a rest-destructure would leave an
  // unused binding, which this repo's lint config rejects).
  const authorLink: DocLink = { ...link };
  delete authorLink.isSourceSop;
  return authorLink;
}
